import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";

function EnquiryRow({ kind, enquiry, onStatus, busy }) {
  const handled = enquiry.status === "handled";
  const detail =
    kind === "interest"
      ? `${enquiry.class_type} — ${enquiry.date} ${enquiry.start_time}`
      : `${enquiry.plan_label}${enquiry.claim_founding ? " · Founding 50" : ""}`;
  return (
    <div
      className={`bg-surface border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 ${
        handled ? "border-line opacity-60" : "border-gold/40"
      }`}
      data-testid={`enquiry-row-${enquiry.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-cream font-semibold">{enquiry.name}</p>
        <p className="text-smoke text-sm truncate">
          {enquiry.email} · {enquiry.phone}
        </p>
        <p className="text-gold text-sm mt-1">{detail}</p>
        {enquiry.notes && <p className="text-smoke text-xs mt-1 italic">"{enquiry.notes}"</p>}
      </div>
      <span
        className={`text-xs uppercase tracking-widest px-3 py-1 border w-fit shrink-0 ${
          handled ? "border-line text-smoke" : "border-gold text-gold"
        }`}
        data-testid={`enquiry-status-${enquiry.id}`}
      >
        {handled ? "Handled" : "Pending"}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => onStatus(kind, enquiry.id, handled ? "pending" : "handled")}
        className="rounded-none border-line text-smoke hover:border-gold hover:text-gold w-full sm:w-auto shrink-0"
        data-testid={`enquiry-toggle-${enquiry.id}`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : handled ? (
          <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</>
        ) : (
          <><Check className="h-3.5 w-3.5 mr-1" /> Mark Handled</>
        )}
      </Button>
    </div>
  );
}

export default function AdminEnquiries() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchEnquiries = () => {
    api
      .get("/admin/enquiries")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(apiErrorMessage(err)));
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const handleStatus = async (kind, id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/enquiries/${kind}/${id}`, { status });
      setData((prev) => ({
        ...prev,
        [kind]: prev[kind].map((e) => (e.id === id ? { ...e, status } : e)),
      }));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  const pendingCount = data.interest.filter((e) => e.status !== "handled").length +
    data.membership.filter((e) => e.status !== "handled").length;

  return (
    <div data-testid="admin-enquiries-page">
      <div className="flex items-center gap-2 mb-2">
        <Inbox className="h-5 w-5 text-gold" />
        <h2 className="font-anton uppercase text-cream text-xl">Enquiries</h2>
        {pendingCount > 0 && (
          <span className="bg-gold text-void text-xs font-bold px-2 py-0.5" data-testid="enquiries-pending-count">
            {pendingCount} pending
          </span>
        )}
      </div>
      <p className="text-smoke text-sm mb-8">
        Every class interest and membership request submitted through the site. Mark them handled once you've been in touch.
      </p>

      <h3 className="font-anton uppercase text-gold text-lg mb-4">Class Interest ({data.interest.length})</h3>
      {data.interest.length === 0 ? (
        <p className="text-smoke text-sm mb-8" data-testid="interest-empty">No class enquiries yet.</p>
      ) : (
        <div className="space-y-3 mb-10" data-testid="interest-list">
          {data.interest.map((e) => (
            <EnquiryRow key={e.id} kind="interest" enquiry={e} onStatus={handleStatus} busy={busyId === e.id} />
          ))}
        </div>
      )}

      <h3 className="font-anton uppercase text-gold text-lg mb-4">Membership Requests ({data.membership.length})</h3>
      {data.membership.length === 0 ? (
        <p className="text-smoke text-sm" data-testid="membership-empty">No membership requests yet.</p>
      ) : (
        <div className="space-y-3" data-testid="membership-list">
          {data.membership.map((e) => (
            <EnquiryRow key={e.id} kind="membership" enquiry={e} onStatus={handleStatus} busy={busyId === e.id} />
          ))}
        </div>
      )}
    </div>
  );
}
